/**
 * Contracts board — three dailies and the weekly Vigil — with the faction reputation
 * strip underneath, because the two are the same loop seen from opposite ends: the board
 * is what a faction wants, the strip is what it thinks of you so far.
 *
 * `/api/contracts` is normalised in `progress-api.ts`, so this file only draws.
 */

import type { VNode } from 'preact';
import { useState } from 'preact/hooks';
import { FACTION_NAMES, FACTION_TIERS, factionTier, type FactionId } from '@stillsun/shared';
import {
  claimContract,
  fetchContracts,
  type ActiveContract,
  type ContractsResponse,
} from '../../net/progress-api.js';
import { session } from '../../net/session.js';
import {
  LEDGER_SILENT,
  ListState,
  Section,
  errText,
  useLoader,
  useProgress,
  wick,
} from './common.js';

export interface ContractsProps {
  nonce: number;
  token: string;
}

const FACTION_ORDER: FactionId[] = ['compact', 'hymnal', 'ferry'];
/** Rep tier names, tier 0..4. Flavour only; the thresholds are in shared. */
const TIER_NAMES = ['Stranger', 'Known', 'Trusted', 'Kin', 'Named'] as const;

function resetsIn(resetsAt: number): string {
  if (resetsAt <= 0) return 'The board resets daily';
  const ms = (resetsAt < 1e11 ? resetsAt * 1000 : resetsAt) - Date.now();
  if (ms <= 0) return 'Resetting now';
  const hours = Math.floor(ms / 3_600_000);
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  return hours > 0
    ? `Resets in ${String(hours)} h ${String(mins)} m`
    : `Resets in ${String(mins)} m`;
}

export function Contracts(props: ContractsProps): VNode {
  const { token } = props;
  const board = useLoader<ContractsResponse>(
    () => fetchContracts(token),
    `contracts:${String(props.nonce)}:${token}`,
  );
  const progress = useProgress(token, props.nonce);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');

  const data = board.data;
  const list = data?.active ?? [];
  const dailies = list.filter((c) => c.cadence !== 'weekly');
  const vigils = list.filter((c) => c.cadence === 'weekly');

  const claim = (c: ActiveContract): void => {
    if (busy.length > 0 || !c.claimable || c.claimed) return;
    setBusy(c.id);
    setNote('');
    // Optimistic: the button flips to "Claimed" on the press.
    board.set(
      data
        ? {
            ...data,
            active: data.active.map((x) =>
              x.id === c.id ? { ...x, claimed: true, claimable: false } : x,
            ),
          }
        : null,
    );
    void (async () => {
      try {
        await claimContract(token, c.id);
        await session.refresh();
        board.reload();
      } catch (err) {
        board.set(data);
        setNote(errText(err, 'The Compact would not pay that out.'));
      } finally {
        setBusy('');
      }
    })();
  };

  const card = (c: ActiveContract): VNode => {
    const pct = Math.max(0, Math.min(100, (c.progress / c.goal) * 100));
    const complete = c.progress >= c.goal;
    return (
      <article
        key={c.id}
        class={`hub-card hub-contract${c.claimed ? ' done' : ''}${complete && !c.claimed ? ' ready' : ''}`}
      >
        <h3 class="hub-card-title">{c.title}</h3>
        <p class="hub-card-blurb">
          {FACTION_NAMES[c.faction as FactionId] ?? c.faction}
          {c.cadence === 'weekly' ? ' · Vigil' : ''}
        </p>
        <div class="hub-progress">
          <div class="hub-progress-bar">
            <div class="hub-progress-fill" style={{ width: `${String(pct)}%` }} />
          </div>
          <span class="hub-progress-num">
            {`${String(Math.min(c.progress, c.goal))} / ${String(c.goal)}`}
          </span>
        </div>
        <div class="hub-card-foot">
          {c.reward > 0 ? <span class="hub-price">{wick(c.reward)} Wick</span> : null}
          {c.claimed ? (
            <span class="hub-done">Claimed</span>
          ) : (
            <button
              type="button"
              class="hub-btn hub-btn-amber"
              disabled={!c.claimable || busy.length > 0}
              onClick={() => {
                claim(c);
              }}
            >
              {busy === c.id ? 'Claiming…' : c.claimable ? 'Claim' : 'In progress'}
            </button>
          )}
        </div>
      </article>
    );
  };

  return (
    <div class="hub-pane">
      {note.length > 0 ? <p class="hub-error">{note}</p> : null}

      <Section title="Contracts" hint={data ? resetsIn(data.resetsAt) : 'The board resets daily'}>
        <ListState
          loading={board.loading}
          offline={!board.loading && data === null}
          empty={list.length === 0}
          emptyText="The board is empty. Someone will want something soon enough."
        />
        <div class="hub-list">{dailies.map(card)}</div>
        {vigils.length > 0 ? (
          <>
            <h3 class="hub-group-title">The Vigil</h3>
            <div class="hub-list">{vigils.map(card)}</div>
          </>
        ) : null}
      </Section>

      <Section title="Standing" hint="Nobody's favour locks out anybody else's">
        {progress.data === null ? (
          <p class="hub-notice">{LEDGER_SILENT}</p>
        ) : (
          <div class="hub-factions">
            {FACTION_ORDER.map((f) => {
              const rep = progress.data?.factions[f] ?? 0;
              const tier = factionTier(rep);
              const floor = FACTION_TIERS[Math.max(0, tier)] ?? 0;
              const ceiling = FACTION_TIERS[tier + 1];
              const pct =
                ceiling === undefined
                  ? 100
                  : Math.max(0, Math.min(100, ((rep - floor) / (ceiling - floor)) * 100));
              return (
                <div key={f} class={`hub-faction ${f}`}>
                  <div class="hub-faction-head">
                    <b>{FACTION_NAMES[f]}</b>
                    <span class="hub-hint">{TIER_NAMES[Math.min(4, tier)] ?? 'Stranger'}</span>
                  </div>
                  <div class="hub-progress-bar">
                    <div class="hub-progress-fill" style={{ width: `${String(pct)}%` }} />
                  </div>
                  <span class="hub-progress-num">
                    {ceiling === undefined
                      ? `${String(rep)} rep — highest standing`
                      : `${String(rep)} / ${String(ceiling)} rep`}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}
